import * as turf from "@turf/turf"
import type { Feature, FeatureCollection, Point, Polygon } from "geojson"
import { memo, useCallback, useMemo, useState } from "react"

import {
	CIRCLE_POLYGON_STEPS,
	DRAW_CIRCLE_LAYER_PAINT,
	DRAW_FILL_LAYER_PAINT,
	DRAW_LINE_LAYER_PAINT,
	LINE_STRING_GEOMETRY_TYPE_FILTER,
	POINT_GEOMETRY_TYPE_FILTER,
	POLYGON_GEOMETRY_TYPE_FILTER,
} from "../constants"
import { MapSlotLayerId } from "../enums"
import { useCurrentMapEvent } from "../hooks"
import type { MapEventHandler } from "../typings"
import { Layer, Source } from "./memo"

const DISC_DRAWER_SOURCE_ID = "disc-drawer-source"

enum DiscDrawerLayerIds {
	CIRCLE = "disc-drawer-circle-layer",
	LINE = "disc-drawer-line-layer",
	FILL = "disc-drawer-fill-layer",
}

interface DiscDrawerProps {
	onComplete?: (disc: Polygon) => void
}

export const DiscDrawer = memo<DiscDrawerProps>((props) => {
	const { onComplete } = props

	const [firstPoint, setFirstPoint] = useState<Feature<Point> | null>(null)
	const [secondPoint, setSecondPoint] = useState<Feature<Point> | null>(null)
	const [tempPoint, setTempPoint] = useState<Feature<Point> | null>(null)

	const reset = useCallback(() => {
		setFirstPoint(null)
		setSecondPoint(null)
		setTempPoint(null)
	}, [])

	const handleMapClick = useCallback<MapEventHandler<"click">>(
		(e) => {
			const { lngLat } = e

			const point = turf.point(lngLat.toArray())

			if (!firstPoint) {
				setFirstPoint(point)
			} else if (!secondPoint) {
				setSecondPoint(point)
			} else {
				const innerCircle = turf.circle(firstPoint, turf.distance(firstPoint, secondPoint), {
					steps: CIRCLE_POLYGON_STEPS,
				})
				const outerCircle = turf.circle(firstPoint, turf.distance(firstPoint, point), {
					steps: CIRCLE_POLYGON_STEPS,
				})
				const disc = turf.mask(innerCircle, outerCircle)

				onComplete?.(disc.geometry)
				reset()
			}
		},
		[firstPoint, onComplete, reset, secondPoint],
	)

	const handleMouseMove = useCallback<MapEventHandler<"mousemove">>(
		(e) => {
			const { lngLat } = e

			if (!firstPoint) return

			const point = turf.point(lngLat.toArray())
			setTempPoint(point)
		},
		[firstPoint],
	)

	useCurrentMapEvent("click", handleMapClick)
	useCurrentMapEvent("mousemove", handleMouseMove)

	const geojson = useMemo<FeatureCollection>(() => {
		const features: Feature[] = []

		if (firstPoint) {
			features.push(firstPoint)
		}

		if (tempPoint) {
			features.push(tempPoint)
		}

		if (firstPoint && !secondPoint && tempPoint) {
			features.push(turf.lineString([firstPoint.geometry.coordinates, tempPoint.geometry.coordinates]))
		}

		if (firstPoint && secondPoint && tempPoint) {
			const innerCircle = turf.circle(firstPoint, turf.distance(firstPoint, secondPoint), {
				steps: CIRCLE_POLYGON_STEPS,
			})
			const outerCircle = turf.circle(firstPoint, turf.distance(firstPoint, tempPoint), {
				steps: CIRCLE_POLYGON_STEPS,
			})
			const disc = turf.mask(innerCircle, outerCircle)
			features.push(disc)
			// @ts-expect-error REASON: sector is a singular Polygon feature
			features.push(turf.polygonToLine(disc))
		}

		return turf.featureCollection(features)
	}, [firstPoint, secondPoint, tempPoint])

	return (
		<Source id={DISC_DRAWER_SOURCE_ID} type="geojson" data={geojson}>
			<Layer
				id={DiscDrawerLayerIds.CIRCLE}
				type="circle"
				paint={DRAW_CIRCLE_LAYER_PAINT}
				filter={POINT_GEOMETRY_TYPE_FILTER}
				beforeId={MapSlotLayerId.DRAW}
			/>
			<Layer
				id={DiscDrawerLayerIds.LINE}
				type="line"
				paint={DRAW_LINE_LAYER_PAINT}
				filter={LINE_STRING_GEOMETRY_TYPE_FILTER}
				beforeId={DiscDrawerLayerIds.CIRCLE}
			/>
			<Layer
				id={DiscDrawerLayerIds.FILL}
				type="fill"
				paint={DRAW_FILL_LAYER_PAINT}
				filter={POLYGON_GEOMETRY_TYPE_FILTER}
				beforeId={DiscDrawerLayerIds.LINE}
			/>
		</Source>
	)
})
DiscDrawer.displayName = "DiscDrawer"
